import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, timeout: 300000, withCredentials: true });

export const TOKEN_KEY = "ssg_token";
export function setToken(t) {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
}
export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

client.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

let onUnauthorized = null;
export function setUnauthorizedHandler(fn) { onUnauthorized = fn; }

client.interceptors.response.use(
  (r) => r,
  (err) => {
    const url = err?.config?.url || "";
    if (err?.response?.status === 401 && !url.includes("/auth/")) {
      setToken(null);
      if (onUnauthorized) onUnauthorized();
    }
    return Promise.reject(err);
  }
);

const api = {
  // auth
  register: (payload) => client.post("/auth/register", payload).then((r) => r.data),
  login: (payload) => client.post("/auth/login", payload).then((r) => r.data),
  logout: () => client.post("/auth/logout").then((r) => r.data),
  me: () => client.get("/auth/me").then((r) => r.data),
  googleSession: (sessionId) => client.post("/auth/google/session", { session_id: sessionId }, { headers: { "X-Session-ID": sessionId } }).then((r) => r.data),
  selfAiAsk: (message) => client.post("/self-ai/ask", { message }).then((r) => r.data),
  selfAiFeedback: (payload) => client.post("/self-ai/feedback", payload).then((r) => r.data),
  // company
  listMembers: () => client.get("/company/members").then((r) => r.data),
  addMember: (payload) => client.post("/company/members", payload).then((r) => r.data),
  removeMember: (id) => client.delete(`/company/members/${id}`).then((r) => r.data),

  getConfig: () => client.get("/config").then((r) => r.data),
  getUsage: () => client.get("/usage").then((r) => r.data),
  resetUsage: () => client.post("/usage/reset").then((r) => r.data),
  listSessions: () => client.get("/sessions").then((r) => r.data),
  getSession: (id) => client.get(`/sessions/${id}`).then((r) => r.data),
  deleteSession: (id) => client.delete(`/sessions/${id}`).then((r) => r.data),
  chat: (payload) => client.post("/chat", payload).then((r) => r.data),
  upload: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post("/upload", fd).then((r) => r.data);
  },
  generateImage: (payload) => client.post("/generate-image", payload).then((r) => r.data),
  deepResearch: (payload) => client.post("/deep-research", payload).then((r) => r.data),
  getResearch: (id) => client.get(`/deep-research/${id}`).then((r) => r.data),
  datasetDetail: (id) => client.get(`/data/${id}`).then((r) => r.data),
  switchSheet: (id, sheet) => client.post(`/data/${id}/sheet`, { sheet }).then((r) => r.data),
  dataQuery: (payload) => client.post("/data/query", payload).then((r) => r.data),
  dataAnalyze: (payload) => client.post("/data/analyze", payload).then((r) => r.data),
  nlFilter: (payload) => client.post("/data/nl-filter", payload).then((r) => r.data),
  pivot: (payload) => client.post("/data/pivot", payload).then((r) => r.data),
  join: (payload) => client.post("/data/join", payload).then((r) => r.data),
  joinCandidates: () => client.get("/join-candidates").then((r) => r.data),
  exportReport: (payload) => client.post("/report/export", payload).then((r) => r.data),
  // developer + agent
  developerRun: (payload) => client.post("/developer/run", payload).then((r) => r.data),
  agentRun: (payload) => client.post("/agent/run", payload).then((r) => r.data),
  agentRuns: () => client.get("/agent/runs").then((r) => r.data),

  billingPlans: () => client.get("/billing/plans").then((r) => r.data),
  createOrder: (planId) => client.post("/billing/create-order", { planId }).then((r) => r.data),
  verifyPayment: (payload) => client.post("/billing/verify", payload).then((r) => r.data),
  billingHistory: () => client.get("/billing/history").then((r) => r.data),

  listViews: (datasetId) => client.get("/views", { params: { datasetId } }).then((r) => r.data),
  createView: (payload) => client.post("/views", payload).then((r) => r.data),
  renameView: (id, name) => client.patch(`/views/${id}`, { name }).then((r) => r.data),
  deleteView: (id) => client.delete(`/views/${id}`).then((r) => r.data),
  listSchedules: () => client.get("/schedules").then((r) => r.data),
  createSchedule: (payload) => client.post("/schedules", payload).then((r) => r.data),
  runScheduleNow: (id) => client.post(`/schedules/${id}/run-now`).then((r) => r.data),
  deleteSchedule: (id) => client.delete(`/schedules/${id}`).then((r) => r.data),
};

export function downloadBase64(b64, filename, mime) {
  const bytes = atob(b64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  const url = URL.createObjectURL(new Blob([arr], { type: mime }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

export default api;
